import { LitElement, html } from 'lit';
import '../../../components/state-badge/agwa-state-badge.js';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import {
  LoginUser,
  LoginUserImis,
  SecurityDomainDto,
} from '../../../api/agwa-api-model';
import { SecurityApi } from '../../../api/security/security-api';
import { WellKnownIds } from '../../../utility/claim-id';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '../../../components/search/agwa-search';
import '../../../components/empty-state/agwa-empty-state';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import { renderOperationTypeChips } from '../../app-members/shared/operation-type-chips';
import { styles } from './staff-list-styles';
import { WithUserProfileConsumer } from '../../../mixins/with-user-profile-consumer';
import { LoginGroup } from '../../../api/agwa-api-model';
import { router } from '../../../router';
import '../../../components/data-table/data-table';
import './staff-row-expander.js';
import type {
  DataTableColumnDef,
  DataTable,
} from '../../../components/data-table/data-table';
import type { DataTableDataProvider } from '../../../components/data-table/data-table-provider';
import { terms } from '../../../language/terms';
import { preservedCasing } from '../../../language/preserved-casing';
import { personName } from '../../../utility/person-name';
import '../../../components/user/username';
import '../../../components/filter/agwa-filter-rail';
import '../../../components/filter/agwa-filter-summary';
import { SEARCH_CLAUSE_KEY } from '../../../components/filter/agwa-filter-summary';
import type {
  FilterFacet,
  FilterSelection,
} from '../../../components/filter/filter-facets';
import {
  isSelectionEmpty,
  selectedValues,
} from '../../../components/filter/filter-facets';
import {
  ACCESS_ACTIVE,
  ACCESS_BLOCKED,
  FACET_ACCESS,
  FACET_TYPES,
} from './staff-list-facets';
import {
  collapseOperationTypeCodes,
  getOrganizationTypeLabel,
} from '../../app-members/shared/organization-type-labels';
import { isSame } from '../../../utility/strings/strings-utils';
// The rail's words come from the same registry the State column's badge reads, so a filter and
// the rows it narrows to can never describe one state with two different words.
import { resolveState } from '../../../utility/state-colours/state-colour-registry';
import {
  getSseHub,
  hubTopics,
  type HubEvent,
} from '../../../utility/notifications/sse-hub';

/** Extended LoginUser with resolved iMIS reference and site-derived operation types */
interface StaffRow extends LoginUser {
  imisReference: string;
  /**
   * What this person IS by virtue of the sites they are assigned to: the WHOLE codes from
   * `GetGroupUsersResult.operationTypesByUser`, uncollapsed, so the type facet can match a Prime
   * System Supplier who is also a Manufacturer. The chips collapse at render time.
   */
  operationTypes: string[];
}

/**
 * The rail's storage key, which is what remembers whether the rail is folded. The workspace tab
 * keeps its own key so folding the rail on a company's Staff tab does not fold it on the AGWA
 * Staff page - they are different working surfaces with different room beside the table (D-102).
 */
const RAIL_KEY_PAGE = 'staff-list';
const RAIL_KEY_TAB = 'staff-list-tab';

@customElement('staff-list')
export class StaffList extends WithUserProfileConsumer(LitElement) {
  @property({ type: String }) groupId = '';
  /** Optional return URL for add/edit navigation. If set, navigations will include this as returnUrl param. */
  @property({ type: String }) returnUrl = '';

  /**
   * Whether this instance is the one hosted on a workspace tab rather than on the AGWA Staff
   * page. It decides ONE thing: which key the rail remembers its folded state under.
   *
   * Declared explicitly by the host rather than inferred from `returnUrl` happening to be set.
   * A return URL is about where the add / edit form goes back to, and reading a rail's storage
   * scope out of it would make the two silently coupled - a host that stopped passing a return
   * URL would start sharing the module page's folded state without anything saying so.
   */
  @property({ type: Boolean, attribute: 'rail-scope-tab' }) railScopeTab =
    false;

  /**
   * Set by a host that already pads its own content, so this list adds no gutter of its own.
   *
   * The company workspace's Staff tab is such a host: its tab panel pads what it holds, and a
   * second gutter drawn here stacks on the first - white space under the tab strip, and the same
   * amount taken off the height the table has to scroll its rows in.
   *
   * DECLARED BY THE HOST rather than detected from an ancestor's class. The rule this replaced
   * read `:host-context(.tab-panel-body)`, a selector only Chromium implements, so everywhere else
   * it quietly matched nothing and the gutters stacked. A component cannot reliably discover where
   * it was put; the one putting it there already knows, and says so - the same reasoning that
   * keeps `railScopeTab` above an explicit flag rather than something inferred from `returnUrl`.
   */
  @property({ type: Boolean, reflect: true }) embedded = false;

  @state() private users: LoginUser[] = [];

  @state() private loading = false;
  @state() private error: string | null = null;

  @state() private search = '';
  /**
   * Which values are ticked in the rail, keyed by facet.
   *
   * One object rather than a field per filter, because the rail and the query sentence both take
   * the whole selection: splitting it would leave two places that can disagree about what is
   * currently applied.
   *
   * A facet with no ticked values narrows nothing, which is what removed the "All" option the old
   * radio group needed - with independent checkboxes the empty set already says "all", and an
   * explicit All is a third control that can contradict the other two.
   *
   * Holds canonical keys, never the words on screen.
   */
  @state() private filterSelection: FilterSelection = {};

  static styles = [styles];
  @state() group: LoginGroup | null = null;
  @state() private imis: LoginUserImis[] = []; // Store imis data
  /**
   * Per user id (lower-cased), the operation-type codes of the sites the user works at, as the
   * server composed them (union of active sites, all-sites expanded). Lower-cased on the way in
   * because the server serialises Guid keys in its own casing and `LoginUser.id` arrives in its
   * own; a lookup that trusted either casing would silently badge nobody.
   */
  @state() private operationTypesByUser: Record<string, string[]> = {};
  /**
   * The domain catalogue, for the short code each chip shows (PSS, PSM, FAB) and the facet's
   * options. The codes are AGWA's own vocabulary and come from the server rather than a client
   * table, so a second copy cannot drift - the same reason the company list reads them.
   */
  @state() private domainCatalogue: SecurityDomainDto[] = [];
  @state() private staffProvider?: DataTableDataProvider<StaffRow>;

  /**
   * How to leave the company topic this list is listening on, and which company that is.
   *
   * Held as a pair because the two have to move together: a list that is re-pointed at another
   * company must leave the first topic before joining the second, or it goes on being refreshed by
   * a roster it has stopped showing.
   */
  private sseUnsubscribe?: () => void;
  private subscribedGroupId = '';

  /**
   * Which roster read is the current one.
   *
   * One action can produce several events - moving a person between sites is a remove and an add -
   * and each starts a read. Without a token the response that happens to land last wins rather than
   * the one that was asked for last, which would leave the screen showing a state the server had
   * already moved on from and no way to tell.
   */
  private rosterReadToken = 0;

  /**
   * How many roster reads have landed, handed to an open person's site panel so it re-reads with
   * the roster (#398 C9). The table keeps an open panel's element across renders, so without this
   * the panel would go on showing the sites and assignments it read when it was opened while the
   * row above it moved on.
   */
  private rosterRevision = 0;

  connectedCallback(): void {
    super.connectedCallback();
    // Re-attached with the same company after being removed from the DOM - a workspace tab being
    // shown again, a host moving the component. `updated` only subscribes on a groupId CHANGE, so
    // without this the roster would be live only until the first time it left the screen.
    if (this.groupId) {
      this.subscribeToAssignmentChanges();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribeFromAssignmentChanges();
  }

  updated(changedProps: Map<string, unknown>) {
    // If groupId explicitly changed and is truthy, fetch
    if (changedProps.has('groupId') && this.groupId) {
      this.subscribeToAssignmentChanges();
      void this.fetchUsers();
    }
    // Fallback: if no groupId provided via route but access has one, adopt it once
    if (!this.groupId && this.access?.groupId) {
      this.groupId = this.access.groupId;
      // fetch will be triggered by groupId change on next update cycle
    }
  }

  /**
   * Listen for site assignments changing anywhere in the company this list is showing.
   *
   * THE ROSTER IS NOT LIVE WITHOUT THIS, and nothing on screen says so. Each row's operation types
   * are composed by the server for the whole roster at load time
   * (`GetGroupUsersResult.operationTypesByUser`), so a person given their first site elsewhere - or
   * granted the right to edit the company's Prime System IP, which is an assignment to a site
   * nobody sees - goes on reading as holding nothing until somebody reloads the page. The event
   * carries the FACT; the list re-reads (staff-operation-types-acceptance.md, rule 7).
   *
   * Scoped to the COMPANY rather than to a site or a person because a roster cannot know in advance
   * which of either is about to change, and because what it shows - the union of types per person,
   * and the facet counts across every row - is derived from every assignment in the company at
   * once.
   *
   * A hub that is not up yet leaves the list working exactly as it did before, which is what the
   * try/catch is for: a roster that cannot subscribe is stale, never broken.
   */
  private subscribeToAssignmentChanges(): void {
    if (this.subscribedGroupId === this.groupId && this.sseUnsubscribe) return;
    this.unsubscribeFromAssignmentChanges();
    if (!this.groupId) return;
    try {
      const hub = getSseHub();
      this.sseUnsubscribe = hub.subscribeTopic(
        hubTopics.groupAssignments(this.groupId),
        (evt: HubEvent) => this.handleAssignmentChanged(evt)
      );
      this.subscribedGroupId = this.groupId;
    } catch {
      // Hub not initialised yet (bootstrap order, or a test mounting the component alone).
    }
  }

  private unsubscribeFromAssignmentChanges(): void {
    this.sseUnsubscribe?.();
    this.sseUnsubscribe = undefined;
    this.subscribedGroupId = '';
  }

  /**
   * A site assignment changed somewhere in this company: read the roster again.
   *
   * RE-READ RATHER THAN PATCH THE ROW THE MESSAGE NAMES. The message carries one assignment, while
   * a row's badge is the union across every site the person holds, so one assignment cannot produce
   * it; and the rail's counts are taken over the whole roster, so patching a single row would leave
   * them wrong - a saving that turns into a visible defect. Composing that union here would also
   * fork a derivation the acceptance document deliberately keeps in one place, on the server.
   *
   * Quietly, because this list is not the one that asked: the rows stay on screen while the read is
   * in flight, so a roster nobody touched does not blink, and an expanded row stays open under the
   * person reading it.
   */
  private handleAssignmentChanged(_evt: HubEvent): void {
    void this.fetchUsers({ quiet: true });
  }

  get isSameGroup() {
    return this.access?.groupId === this.groupId;
  }
  get isSystemTenant() {
    return this.access?.hasRole('systemTenant') ?? false;
  }
  get canAdministerUsers() {
    if (!this.isSystemTenant) {
      return false;
    }
    if (!this.access) return false;
    if (this.isSameGroup && !this.access.isStaffAdmin) return false;
    return (
      this.access.isAdmin || this.access.isGroupAdmin || this.access.isUserAdmin
    );
  }

  /** Check if the current group is the internal AGWA group (seeded system tenant) */
  get isInternalGroup(): boolean {
    return this.groupId.toLowerCase() === WellKnownIds.agwaGroupId;
  }

  /**
   * Read the company's roster and the domain catalogue, and rebuild the rows from them.
   *
   * `quiet` is a refresh this list did not ask for - a site assignment changed somewhere in the
   * company, possibly in another browser. It differs in what it does to the screen WHILE the read
   * is in flight, never in what it reads: the rows, the provider and the loading state are left
   * alone, so the table is not torn down and rebuilt under someone who is reading it. Tearing it
   * down is what the ordinary path does, and it is right there - a list pointed at a different
   * company must not show the previous one's people for even one frame - but here it would blink
   * the whole roster and close any row the reader had expanded.
   */
  async fetchUsers(options: { quiet?: boolean } = {}) {
    const quiet = options.quiet === true;
    // Taken BEFORE the await so that two reads started in either order are resolved by which was
    // asked for last, not by which server round-trip happened to finish first.
    const token = ++this.rosterReadToken;
    if (!quiet) {
      this.loading = true;
      this.error = null;
      this.users = [];
      this.staffProvider = undefined;
    }
    try {
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      // The catalogue is fetched beside the roster rather than after it, so the chips and the
      // facet are ready on the same render the rows arrive on.
      const [resp, domainsResp] = await Promise.all([
        api.getGroupUsers({
          loginGroupId: this.groupId,
          includeSecurityContexts: false, // Staff list doesn't need security contexts
        }),
        ApiFactory.authenticated(SecurityApi).getSecurityDomains(),
      ]);
      // A read that has been overtaken is dropped whole rather than half-applied: its roster, its
      // catalogue and its error all describe a moment that is no longer the one on screen.
      if (token !== this.rosterReadToken) return;
      if (resp.success && resp.data?.users) {
        this.users = resp.data.users;
        this.group = resp.data.group ?? null;
        this.imis = resp.data.imis || [];
        this.operationTypesByUser = Object.fromEntries(
          Object.entries(resp.data.operationTypesByUser ?? {}).map(
            ([userId, codes]) => [userId.toLowerCase(), codes]
          )
        );
        // A failed catalogue read degrades the chips to full labels and empties the facet; it does
        // not take the roster down with it, which is what the company list does too.
        this.domainCatalogue = domainsResp.success
          ? (domainsResp.data?.domains ?? [])
          : [];
        this.rosterRevision++;
        this.createStaffProvider();
      } else if (!quiet) {
        this.error =
          resp.error || `Failed to load ${terms.loginUser.lowerPlural}`;
      }
    } catch (e) {
      // A refresh nobody asked for keeps what is on screen when it fails. Replacing a roster the
      // reader is using with an error banner, because a background read did not come back, takes
      // away more than it reports - and the list was no worse off before the event arrived. The
      // next assignment anywhere in the company brings another one.
      if (!quiet) {
        this.error =
          (e as Error)?.message ||
          `Error loading ${terms.loginUser.lowerPlural}`;
      }
    } finally {
      // Only the read that is still the current one may say the list has stopped loading: an
      // overtaken read reporting it would clear the spinner over rows that have not arrived.
      if (token === this.rosterReadToken) {
        this.loading = false;
      }
    }
  }

  /** Column definitions for the staff table */
  private get staffColumns(): DataTableColumnDef[] {
    const columns: DataTableColumnDef[] = [];

    // Only show expand column for non-internal groups (member groups with sites)
    if (!this.isInternalGroup) {
      columns.push({
        title: '',
        fieldName: '_expand',
        width: '50px',
        fixed: 'left',
        displayValue: (table: DataTable, item: StaffRow) => {
          const isExpanded = table.isExpanded(item);
          return html`<sl-icon-button
            name="${isExpanded ? 'chevron-down' : 'chevron-right'}"
            label="${isExpanded ? 'Collapse' : 'Expand'}"
            @click=${(e: Event) => {
              // Don't let expand toggle also open the user.
              e.stopPropagation();
              this.toggleExpand(table, item);
            }}
          ></sl-icon-button>`;
        },
      });
    }

    columns.push(
      {
        title: 'Username',
        fieldName: 'username',
        width: '200px',
        sortable: true,
        fixed: 'left',
      },
      {
        title: preservedCasing(`${terms.imis.value} Ref`),
        fieldName: 'imisReference',
        width: '120px',
        fixed: 'left',
      },
      {
        title: 'Email',
        fieldName: 'emailAddress',
        width: '280px',
      },
      {
        title: 'Display Name',
        fieldName: 'displayName',
        width: '220px',
        // The person named by id through the directory (D-151, contact rule 8); the Username
        // column beside it stays, as the account's login identifier being administered.
        displayValue: (_table: DataTable, item: StaffRow) =>
          html`<user-name .userId=${item.id}></user-name>`,
      },
      // AGWA's own staff are not members of anything: their one site holds access grants, not
      // operation types, so the column would be blank on every row. Hidden for the same reason
      // the expand column is.
      ...(this.isInternalGroup
        ? []
        : [
            {
              title: terms.userGroupDomain.plural,
              fieldName: 'operationTypes',
              width: '160px',
              displayValue: (_table: DataTable, item: StaffRow) =>
                this.renderOperationTypes(item),
            },
          ]),
      {
        title: 'State',
        fieldName: 'locked',
        width: '100px',
        displayValue: (_table: DataTable, item: StaffRow) =>
          html`<agwa-state-badge
            domain="staffAccess"
            state=${item.locked ? 'Blocked' : 'Active'}
          ></agwa-state-badge>`,
      }
    );

    return columns;
  }

  /**
   * The person's operation types as short-code chips, the shape the company list draws.
   *
   * ONLY THE TYPES THAT CARRY A SHORT CODE, as the site header strip decides it: a site can also
   * hold an access grant (AGWA Head Office holds the Glass auditor scope), and that is what the
   * person may DO there, not what they ARE. The catalogue marks the operation types with a short
   * code and nothing else, so its presence is the test; a catalogue that failed to load shows no
   * chips rather than a wrong set. Blank when a person holds no site, matching the company list's
   * blank-when-none ruling (`@CO-TYPES-006`): on a list where holding none is ordinary, a printed
   * negative reads as noise. The pair collapses here and not in the data, so the facet can still
   * match on the whole set. A legend of every code opens over the chips in the top layer
   * (operation-type-chips.ts, U33 item 7).
   */
  private renderOperationTypes(item: StaffRow) {
    const codes = collapseOperationTypeCodes(item.operationTypes).filter(
      (code) => this.catalogueEntry(code)?.shortCode
    );
    if (codes.length === 0) {
      return html``;
    }
    return renderOperationTypeChips(
      codes.map((code) => ({
        code: this.shortCodeFor(code),
        label: getOrganizationTypeLabel(code),
      }))
    );
  }

  private catalogueEntry(code: string): SecurityDomainDto | undefined {
    return this.domainCatalogue.find((d) => isSame(d.code, code));
  }

  /** The catalogue's short code for a domain, or its full label when the catalogue has none. */
  private shortCodeFor(code: string): string {
    return (
      this.catalogueEntry(code)?.shortCode || getOrganizationTypeLabel(code)
    );
  }

  /**
   * The types the facet offers: the operation types, which are the catalogue entries carrying a
   * short code - the same test the chips apply, so the rail cannot offer a value no chip could
   * wear. Narrowed further to what AGWA actually assigns (`uiAssignable`), as the company list is.
   */
  private get availableDomains(): SecurityDomainDto[] {
    return this.domainCatalogue.filter(
      (d) => d.uiAssignable && d.code && d.shortCode
    );
  }

  /**
   * WHICH PERSON A ROW IS, for a table that has to recognise them again after a re-read.
   *
   * A CLASS FIELD RATHER THAN AN INLINE ARROW, which is what the component's own guidance asks for
   * where the host re-renders often (`data-table.ts`, on `keyevent`). `.keyevent` is a reactive
   * property, so a fresh function on each render reads to Lit as a changed value and costs the
   * table an extra render pass that can change nothing. This list re-reads its whole roster on
   * every assignment made anywhere in the company, so "often" is the company's own activity rather
   * than this screen's. `agwa-audit-trail.rowKey` is the same declaration for the same reason.
   */
  private rowKey = (item: StaffRow): string => item.id;

  /**
   * Open or close one person's site panel under their row.
   *
   * THE ROW IS IDENTIFIED BY ITS PERSON, NOT BY THE OBJECT DRAWING IT, which is what `rowKey`
   * above supplies to the table's `.keyevent`. `DataTable.getPrimaryKey` falls back to the item itself when no
   * key function is given, and `staffRows` rebuilds every row object on each read - so a roster
   * that re-read itself would leave an open panel keyed to an object the table no longer has, and
   * the panel would shut under the person reading it the moment anyone anywhere in the company was
   * assigned to a site. `.keyevent` is the convention the lookup tables and the company list
   * already use for this, and now the only one: the `primaryKey` attribute that used to sit beside
   * it was never a property of the component and did nothing, so it was deleted here and at the
   * twenty other call sites that carried it (architect, 2026-09-19).
   */
  private toggleExpand(table: DataTable, item: StaffRow) {
    if (table.isExpanded(item)) {
      table.remExtension(item);
    } else {
      table.addExtension(
        item,
        () =>
          html`<staff-row-expander
            .userId=${item.id}
            .groupId=${this.groupId}
            .userName=${personName(item.displayName)}
            .rosterRevision=${this.rosterRevision}
          ></staff-row-expander>`
      );
    }
  }

  /**
   * Every staff member as a table row, with the iMIS reference resolved onto it.
   *
   * Resolved once here rather than inside the filter, because the search box reaches the
   * reference: a row that only gains it at render time could not be searched by it.
   */
  private get staffRows(): StaffRow[] {
    return this.users.map((u) => ({
      ...u,
      imisReference: this.getImisReference(u),
      operationTypes:
        this.operationTypesByUser[(u.id ?? '').toLowerCase()] ?? [],
    }));
  }

  /**
   * The staff surviving a given query.
   *
   * Parameterised on the selection rather than reading `this.filterSelection` directly, because
   * the counts in the rail need this same predicate run with the access facet's OWN ticks
   * ignored. Counted any other way, ticking Active renders Blocked as `0` - nobody is in two
   * access states at once - and a count reading zero beside a value you can still usefully tick
   * looks like a broken screen.
   *
   * This is the ONE predicate: the data provider below slices its result, so the sentence's
   * count, the empty state and the rows on screen cannot disagree about what the query means.
   */
  private filterStaff(
    selection: FilterSelection,
    searchText: string
  ): StaffRow[] {
    const text = searchText.trim().toLowerCase();
    const access = selectedValues(selection, FACET_ACCESS);
    const types = selectedValues(selection, FACET_TYPES);

    return this.staffRows.filter((u) => {
      if (
        text &&
        !u.username?.toLowerCase().includes(text) &&
        !u.emailAddress?.toLowerCase().includes(text) &&
        !u.displayName?.toLowerCase().includes(text) &&
        !u.imisReference?.toLowerCase().includes(text)
      ) {
        return false;
      }
      // Nothing ticked and both ticked ask the same question - "Active or Blocked" is everyone -
      // and the predicate must not distinguish them, which an empty facet already handles by
      // narrowing nothing.
      if (
        access.length > 0 &&
        !access.includes(u.locked ? ACCESS_BLOCKED : ACCESS_ACTIVE)
      ) {
        return false;
      }
      // Matched on the WHOLE set, not the collapsed chips: a Prime System Supplier who is also a
      // Manufacturer wears one chip but is genuinely both, and a filter on Supplier must find them.
      if (
        types.length > 0 &&
        !u.operationTypes.some((code) => types.some((t) => isSame(code, t)))
      ) {
        return false;
      }
      return true;
    });
  }

  /** Creates the data provider over the rows the current query keeps. */
  private createStaffProvider() {
    const staffRows = this.filterStaff(this.filterSelection, this.search);

    this.staffProvider = async (request) => {
      const start = request.sliceStart ?? 0;
      const pageSize = request.sliceLength ?? staffRows.length;
      const page = staffRows.slice(start, start + pageSize);
      return {
        items: page,
        totalCount: staffRows.length,
        hasMore: start + page.length < staffRows.length,
      };
    };
  }

  /** How many staff the current query shows, for the sentence above the list. */
  private get shownCount(): number {
    return this.filterStaff(this.filterSelection, this.search).length;
  }

  /** Whether the query has emptied a list that does hold rows, as opposed to an empty list. */
  private get hasNoMatches(): boolean {
    if (this.users.length === 0) return false;
    return this.shownCount === 0;
  }

  /** Whether anything at all is narrowing the list, the search box included. */
  private get anyFilterApplied(): boolean {
    return this.search.trim() !== '' || !isSelectionEmpty(this.filterSelection);
  }

  /**
   * The facets the rail renders, with a count beside every value.
   *
   * Each count is taken with the facet's OWN ticks ignored - see `filterStaff`. The two options
   * are declared in a fixed order rather than derived from the rows, so the rail does not
   * reshuffle itself as staff are added or filtered, and both stay offered when one of them
   * currently matches nobody.
   *
   * The labels come from the state-colour registry, which is what the State column's badge reads,
   * so the filter and the chip beside it cannot spell one state two ways.
   */
  private get filterFacets(): FilterFacet[] {
    const countBy = (facetKey: string, predicate: (u: StaffRow) => boolean) => {
      const without: FilterSelection = {
        ...this.filterSelection,
        [facetKey]: [],
      };
      return this.filterStaff(without, this.search).filter(predicate).length;
    };

    return [
      {
        key: FACET_ACCESS,
        title: 'Access',
        phrase: 'whose access is',
        options: [
          {
            value: ACCESS_ACTIVE,
            label: resolveState('staffAccess', 'Active').label,
            count: countBy(FACET_ACCESS, (u) => !u.locked),
          },
          {
            value: ACCESS_BLOCKED,
            label: resolveState('staffAccess', 'Blocked').label,
            count: countBy(FACET_ACCESS, (u) => !!u.locked),
          },
        ],
      },
      // The facet follows the column: AGWA's own staff have no operation types to be narrowed by.
      ...(this.isInternalGroup
        ? []
        : [
            {
              key: FACET_TYPES,
              title: terms.userGroupDomain.plural,
              phrase: 'working as',
              // Labelled through the SAME function the row chips' tooltip uses, so the filter
              // and the chips cannot disagree about what a type is called.
              options: this.availableDomains.map((d) => ({
                value: d.code,
                label: getOrganizationTypeLabel(d.code),
                count: countBy(FACET_TYPES, (u) =>
                  u.operationTypes.some((code) => isSame(code, d.code))
                ),
              })),
            },
          ]),
    ];
  }

  /**
   * A box was ticked or unticked. The rail hands back the whole selection rather than one change,
   * so there is nothing to merge here and no chance of this page's copy drifting from the
   * control's.
   */
  private handleFilterChange(e: CustomEvent<{ selection: FilterSelection }>) {
    this.filterSelection = e.detail.selection;
    this.createStaffProvider();
  }

  /**
   * Everything narrowing the list is undone at once - the ticks AND the search box.
   *
   * Clearing only the ticks would be the more literal reading of a control that sits in the rail,
   * and it would be wrong: someone who has searched and filtered and wants the full list back has
   * one thing in mind, and leaving the search applied hands them a list still narrowed by
   * something they just asked to clear.
   */
  private handleFilterClear() {
    this.filterSelection = {};
    this.search = '';
    this.createStaffProvider();
  }

  /** One condition was dropped from the query sentence, leaving the rest of the query standing. */
  private handleFilterDrop(e: CustomEvent<{ key: string }>) {
    if (e.detail.key === SEARCH_CLAUSE_KEY) {
      this.search = '';
    } else {
      this.filterSelection = { ...this.filterSelection, [e.detail.key]: [] };
    }
    this.createStaffProvider();
  }

  /**
   * Narrows the list to the search the box reports, once. The box reports one search twice -
   * `search-input` as the person types and `search-change` when they press Enter or clear - so a
   * value already searched is not searched again, which reloaded the table a second time
   * (#398 C15 d). A clear is reported as `search-clear` and then a change to nothing, so the change
   * finds the box already empty and is the one dropped.
   */
  private handleSearchInput(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.search) return;
    this.search = e.detail.value;
    this.createStaffProvider();
  }

  private handleSearchClear() {
    this.search = '';
    this.createStaffProvider();
  }

  // Helper to get iMIS reference for a user
  getImisReference(user: LoginUser): string {
    const imis = this.imis.find((i) => i.loginUserId === user.id);
    return imis ? imis.reference : '';
  }

  // legacy inline search handler removed; using <agwa-search> events now

  navigateToAddUser() {
    const returnParam = this.returnUrl
      ? `&returnUrl=${encodeURIComponent(this.returnUrl)}`
      : '';
    void router?.navigate(
      `/settings/staff/add?groupId=${this.groupId}${returnParam}`
    );
  }

  navigateToEditUser(user: LoginUser) {
    const returnParam = this.returnUrl
      ? `&returnUrl=${encodeURIComponent(this.returnUrl)}`
      : '';
    void router?.navigate(
      `/settings/staff/edit/${user.id}?groupId=${this.groupId}${returnParam}`
    );
  }

  render() {
    return html`<div class="staff-list fill-height-page">
      <div class="user-filter-bar layout-row-wrap fill-height-header">
        <div class="filters-left layout-row-gap">
          <agwa-search
            .value=${this.search}
            placeholder="Search ${terms.loginUser.lowerPlural}..."
            style="--agwa-search-max-width:260px;"
            @search-input=${this.handleSearchInput}
            @search-change=${this.handleSearchInput}
            @search-clear=${this.handleSearchClear}
          ></agwa-search>
        </div>
        <div class="actions-right">
          <sl-button
            variant="primary"
            size="small"
            @click=${this.navigateToAddUser}
          >
            <sl-icon slot="prefix" name="person-plus"></sl-icon>
            ${`Add ${terms.loginUser.value}`}
          </sl-button>
        </div>
      </div>
      <!--
        The rail is a SIBLING of the table, not a strip above it. Its one value pair is legible
        without opening anything, and ticking one changes a checkbox that was already on screen -
        so unlike the radio group this replaces, the filters occupy the same space before and
        after a choice and the rows beneath never move.
      -->
      <div class="list-body">
        <agwa-filter-rail
          storage-key=${this.railScopeTab ? RAIL_KEY_TAB : RAIL_KEY_PAGE}
          .facets=${this.filterFacets}
          .selection=${this.filterSelection}
          ?can-clear=${this.anyFilterApplied}
          @filter-change=${this.handleFilterChange}
          @filter-clear=${this.handleFilterClear}
        ></agwa-filter-rail>
        <div class="list-main">
          <!--
            What the ticks MEAN together, in words. The rail can show which boxes are set; only
            this can show that the search and the access filter are combined, which is what
            someone needs when a person they expected is not in the list.
          -->
          <agwa-filter-summary
            .facets=${this.filterFacets}
            .selection=${this.filterSelection}
            search-text=${this.search}
            subject=${terms.loginUser.lowerPlural}
            shown-count=${this.shownCount}
            total-count=${this.users.length}
            @filter-drop=${this.handleFilterDrop}
          ></agwa-filter-summary>
          ${
            this.loading
              ? html`<agwa-empty-state
                  icon="hourglass-split"
                  title="Loading"
                  message="Fetching ${terms.loginUser.lowerPlural}..."
                ></agwa-empty-state>`
              : this.error
                ? html`<agwa-empty-state
                    icon="exclamation-triangle"
                    title="Error"
                    .message=${this.error}
                  ></agwa-empty-state>`
                : this.users.length === 0 && !this.search
                  ? html`<agwa-empty-state
                      icon="people"
                      title="No ${terms.staffUser.lowerPlural} yet"
                      message="Add your first ${terms.loginUser.lower} to get started."
                    >
                      <sl-button
                        variant="primary"
                        size="small"
                        @click=${this.navigateToAddUser}
                      >
                        <sl-icon slot="prefix" name="person-plus"></sl-icon>Add
                        ${terms.loginUser.value}
                      </sl-button>
                    </agwa-empty-state>`
                  : html`
                      ${
                        this.hasNoMatches
                          ? html`<agwa-empty-state
                              icon="search"
                              title="No matches"
                              message="No ${terms.loginUser.lowerPlural} match every condition above. Remove one, or clear them all."
                            >
                              <!--
                                Clears the ticks AND the search, matching the rail's own control.
                                Offering only "clear search" here would hand back a list still
                                narrowed by a filter the person had just asked to be rid of.
                              -->
                              <sl-button
                                size="small"
                                @click=${this.handleFilterClear}
                                >Clear all filters</sl-button
                              >
                            </agwa-empty-state>`
                          : ''
                      }
                      ${
                        this.staffProvider
                          ? html`<agwa-lit-table
                              fill-height
                              .columns=${this.staffColumns}
                              .dataProvider=${this.staffProvider}
                              .keyevent=${this.rowKey}
                              .clickrows=${true}
                              style=${this.hasNoMatches ? 'display: none' : ''}
                              @rowclick=${(
                                e: CustomEvent<{ item: StaffRow }>
                              ) => this.navigateToEditUser(e.detail.item)}
                            ></agwa-lit-table>`
                          : html``
                      }
                    `
          }
        </div>
      </div>
    </div> `;
  }
}
